import React, { useState, useRef, useEffect, useContext } from "react";
import { DeSoIdentityContext } from "react-deso-protocol";
import { deso_graphql } from "../utils/graphql";
import { createUserAssociation, deleteUserAssociation, getUserAssociations, identity, uploadImage } from "deso-protocol";
import { localFees, systemKey } from "..";
import { Alert, Button, Card, Col, Container, Dropdown, Form, InputGroup, Modal, Row, Spinner } from "react-bootstrap";
import { handleLogin, UserDropdown } from "../appLoader";
import { localCurrency } from "../utils/currency";
import { useUserPreferences } from "../contexts/UserPreferences";
import ExchangeRatesContext from "../contexts/ExchangeRatesContext";
import { AccessGroupsContext } from "../contexts/AccessGroups";

export const formatBytes = (bytes) => {
    if (isNaN(bytes) || bytes === 0) return "0 B";

    const units = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const size = bytes / Math.pow(1024, i);
    const decimals = i === 0 ? 0 : i === 1 ? 1 : 2;

    return `${size.toFixed(decimals)} ${units[i]}`;
};

export const getFormattedFileSize = (dataUri) => {
    if (!dataUri?.startsWith("data:")) return "Unknown";

    try {
        const base64 = dataUri.split(",")[1];
        const byteLength = (base64.length * 3) / 4 - (base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0);
        return formatBytes(byteLength);
    } catch (err) {
        console.error("Error calculating file size:", err);
        return "Unknown";
    }
};

export const getFileIcon = (extraData) => {
    // Try to get the extension from extraData
    const extension = extraData?.extension || extraData?.filename?.split('.').pop() || extraData?.ImageURL?.split('.').pop() || 'unknown';

    // Simple icon map for common extensions
    const iconMap = {
        'docx': 'file-word-fill',  // Word document
        'pdf': 'file-pdf-fill',  // PDF
        'webp': 'file-earmark-image',
        'jpg': 'file-earmark-image',  // Image (JPG)
        'jpeg': 'file-earmark-image',  // Image (JPEG)
        'png': 'file-earmark-image',  // Image (PNG)
        'zip': 'file-earmark-zip-fill',  // ZIP archive
        'txt': 'file-earmark-text-fill',  // Text file
        'json': 'file-earmark-code-fill',  // JSON file
        'mp3': 'file-earmark-music-fill',  // Music file
        'mp4': 'file-earmark-play-fill',  // Video file
    };

    // If the extension is recognized, return the mapped icon
    if (iconMap[extension]) {
        return `bi bi-${iconMap[extension]}`;
    }

    // If it's an unrecognized extension, fall back to 'filetype-{extension}' icon
    if (extension && extension !== 'unknown') {
        return `bi bi-filetype-${extension}`;
    }

    // If no valid extension, use a generic 'file-earmark' icon
    return 'bi bi-file-earmark-fill';
};

export const generateUniqueId = () => {
    if (window.crypto && window.crypto.getRandomValues) {
        // Use crypto.getRandomValues for secure randomness (if available)
        const array = new Uint8Array(32); // 32 bytes = 256 bits (64 characters in hex)
        crypto.getRandomValues(array);
        return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
    } else {
        // Fallback: Use timestamp + random string for generating a unique ID
        const timestamp = Date.now().toString(36); // Current timestamp in base36 (8-9 characters)
        const randomPart = Math.random().toString(36).substr(2, 47); // Generate a longer random string (47 characters)
        return timestamp + randomPart; // Combine timestamp and random part to get a 64-character string
    }
};


export const getFilenameWithoutExtension = (name) => {
    if (typeof name !== "string") return "";
    const lastDotIndex = name.lastIndexOf(".");
    return lastDotIndex > 0 ? name.substring(0, lastDotIndex) : name;
};

export const getFileExtension = (name) => {
    if (typeof name !== "string") return "";
    const lastDotIndex = name.lastIndexOf(".");
    return lastDotIndex > 0 && lastDotIndex < name.length - 1
        ? name.substring(lastDotIndex + 1).toLowerCase()
        : "";
};


export const encryptString = async (string, accessGroup = null, accessGroups) => {
    const { AccessGroupsOwned = [], AccessGroupsMember = [] } = accessGroups;

    // Use 'default-key' if accessGroup is null or undefined
    const groupToFind = accessGroup ?? 'default-key';

    // Combine AccessGroupsOwned and AccessGroupsMember into a single array
    const allAccessGroups = [...AccessGroupsOwned, ...AccessGroupsMember];

    // Find the group with AccessGroupKeyName matching the provided accessGroup (or 'default-key')
    const AccessGroupPublicKeyBase58Check = allAccessGroups.find(
        (group) => group.AccessGroupKeyName === groupToFind
    )?.AccessGroupPublicKeyBase58Check;

    if (!AccessGroupPublicKeyBase58Check) {
        console.error(`Error: '${groupToFind}' AccessGroup not found`);
        return;
    }

    const postData = {
        ...string
    };

    const encryptedPostData = await identity.encryptMessage(AccessGroupPublicKeyBase58Check, JSON.stringify(postData));

    console.log("Encryption Test: data, accessGroup, accessGroups, encryptedPostData", string, groupToFind, accessGroups, encryptedPostData);
    console.log("Length of string to encrypt:", string.length);
    console.log("Length of encrypted string:", encryptedPostData.length);
    return encryptedPostData;
}